import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId } from "../lib/smoothScroll";

const EquipmentCard = ({ item, index }) => (
  <motion.article
    data-testid={`catalog-item-card-${item.slug}`}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="group relative bg-[#231e16] border border-amber-500/15 hover:border-amber-400/40 hover:-translate-y-1 rounded-none overflow-hidden shadow-md hover:shadow-[0_12px_36px_rgba(212,163,89,0.12)] transition-all duration-300"
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={item.image}
        alt={item.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
      />
      <span className="absolute top-4 left-4 text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 bg-[#181410]/80 backdrop-blur border border-amber-500/25 text-amber-300">
        {item.badge}
      </span>
    </div>
    <div className="p-6">
      <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#FBF9F5]">{item.name}</h3>
      <p className="mt-3 text-xs font-mono text-amber-400/80 leading-relaxed">{item.specs}</p>
      <p className="mt-3 text-sm text-stone-400 leading-relaxed">{item.desc}</p>
      <div className="mt-5 flex items-center justify-end border-t border-amber-500/10 pt-4">
        <button
          data-testid={`catalog-item-quote-${item.slug}`}
          onClick={() => scrollToId("#preventivo")}
          aria-label={`Preventivo per ${item.name}`}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-amber-500/25 text-amber-300 group-hover:bg-amber-400 group-hover:text-[#181410] group-hover:border-amber-400 transition-all duration-300"
        >
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </motion.article>
);

export const Catalog = () => {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const category = CATEGORIES.find((c) => c.id === active);

  return (
    <section id="catalogo" data-testid="catalog-section" className="relative py-24 sm:py-32 bg-[#181410]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Il Catalogo"
          title="Catalogo Attrezzature & Luci a Noleggio"
          subtitle="Tecnologia professionale senza cavi a vista, estetica senza compromessi."
          testId="catalog-title"
        />

        <Reveal delay={0.25} className="mt-12">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="catalog-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`shrink-0 px-5 py-2.5 rounded-full text-sm tracking-wide border transition-all duration-300 min-h-[44px] ${
                  active === c.id
                    ? "bg-amber-400 text-[#181410] border-amber-400 font-medium"
                    : "border-amber-500/20 text-stone-300 hover:border-amber-400/50 hover:text-amber-300"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <p data-testid="catalog-category-desc" className="mt-8 max-w-2xl text-sm sm:text-base text-stone-400 leading-relaxed">
              {category.description}
            </p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {category.items.map((item, i) => (
                <EquipmentCard key={item.slug} item={item} index={i} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
